/**
 * Utility functions for calculating missing translation keys
 */

import { TranslationKey } from '../types/translation-types';

/**
 * Find all translation keys that are missing a value for a specific language
 * @param allTranslations - Array of all translation keys with their values
 * @param languageCode - The language code to check for missing translations (e.g., 'en', 'he')
 * @returns Array of key names that don't have a translation for the specified language, sorted alphabetically
 */
export function findMissingKeysForLanguage(
  allTranslations: TranslationKey[],
  languageCode: string
): string[] {
  const missingKeys: string[] = [];

  for (const translationKey of allTranslations) {
    // Check if this translation key has a value for the specified language
    const hasValueForLanguage = translationKey.values.some(
      (value) => value.languageCode === languageCode
    );

    // If no value exists for this language, add the key name to missing keys
    if (!hasValueForLanguage) {
      missingKeys.push(translationKey.keyName);
    }
  }

  // Sort alphabetically for consistent display
  return missingKeys.sort();
}

/**
 * Generate a summary string for missing keys display
 * @param missingCount - Number of missing keys
 * @param totalKeys - Total number of translation keys
 * @returns Formatted string showing count and percentage (e.g., "5 of 500 keys (1%)")
 */
export function getMissingKeySummary(
  missingCount: number,
  totalKeys: number
): string {
  if (totalKeys === 0) {
    return '0 of 0 keys';
  }

  const percentage = Math.round((missingCount / totalKeys) * 100);
  return `${missingCount} of ${totalKeys} keys (${percentage}%)`;
}
